import { copyMarkdownToClipboard } from '@platform/markdown';
import SlackishMarkdown from '@platform/markdown/SlackishMarkdown/SlackishMarkdown';
import { selectConversationDisplayMode } from '@platform/timeline';
import { Icon, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@shared/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { registerWidgetRenderer } from '../registry';
import type { EditableSummaryEntity, TimelineWidgetProps } from '../types';

interface EditableSummaryWidgetProps extends TimelineWidgetProps<EditableSummaryEntity> {
  debugMode?: boolean;
  hideTimestamp?: boolean;
  assistantBgColor?: string;
}

registerWidgetRenderer(
  'editable_summary',
  ({ entity, debugMode, hideTimestamps, assistantBgColor, convId }) => (
    <EditableSummaryWidget
      key={entity.id}
      entity={entity as EditableSummaryEntity}
      convId={convId}
      debugMode={debugMode}
      hideTimestamp={hideTimestamps}
      assistantBgColor={assistantBgColor}
    />
  ),
  { visibility: { normal: true, debug: true } }
);

function EditableSummaryWidget({
  entity,
  convId,
  debugMode = false,
  hideTimestamp = false,
  assistantBgColor = '#F5F5F5',
}: EditableSummaryWidgetProps) {
  const { title, content, chunks, status, error } = entity.props;
  const displayMode = useSelector(selectConversationDisplayMode(convId));

  if (process.env.NODE_ENV === 'development' && !convId) {
    console.warn(
      'EditableSummaryWidget: convId is required for displayMode feature to work correctly'
    );
  }

  const shouldHideInChat = displayMode === 'ui';

  const aggregated = useMemo(() => {
    const base = typeof content === 'string' ? content : '';
    const chunkText = Array.isArray(chunks) ? chunks.join('') : '';
    return base + chunkText;
  }, [content, chunks]);

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    if (!aggregated) return;
    try {
      await copyMarkdownToClipboard(aggregated);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy summary', err);
    }
  }, [aggregated]);

  if (shouldHideInChat) {
    return null;
  }

  const isStreaming = !aggregated && status === 'active';
  const label = title || 'Summary';
  const chunkCount = Array.isArray(chunks) ? chunks.length : 0;

  return (
    <div className="flex justify-start w-full min-w-0">
      <div className="flex flex-col gap-2 min-w-0" style={{ maxWidth: '80%' }}>
        <Paper
          elevation={1}
          sx={{
            py: '14px',
            px: 2,
            bgcolor: assistantBgColor,
          }}
          className="min-w-0"
        >
          <div className="flex items-start gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <Typography variant="caption" color="text.secondary" className="block mb-1">
                {label}
              </Typography>
              {aggregated ? (
                <div
                  className="prose prose-sm max-w-none mp-block artifact-content"
                  style={{ fontWeight: 500 }}
                >
                  <SlackishMarkdown text={aggregated} />
                </div>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {status === 'error'
                    ? error || 'Unable to generate summary.'
                    : 'Waiting for summary...'}
                </Typography>
              )}
              {!hideTimestamp && (
                <Typography variant="caption" color="text.secondary" className="block mt-1">
                  {new Date(entity.timestamp).toLocaleTimeString()}
                </Typography>
              )}
            </div>
            <Tooltip title={copied ? 'Copied!' : 'Copy summary'}>
              <span>
                <IconButton
                  aria-label="Copy summary to clipboard"
                  size="small"
                  onClick={handleCopy}
                  disabled={!aggregated}
                >
                  <Icon fontSize="small">content_copy</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </div>
          {status === 'error' && error && (
            <Typography variant="caption" color="error" className="block mt-1">
              {error}
            </Typography>
          )}
        </Paper>
        {isStreaming && (
          <div className="flex items-center gap-2 px-2">
            <LinearProgress style={{ flexGrow: 1, maxWidth: 200 }} />
            <span className="text-xs text-gray-500">Summarizing...</span>
          </div>
        )}
        {debugMode && (
          <div className="flex flex-wrap gap-1 px-2">
            <Typography variant="caption" color="text.secondary">
              status: {status || 'active'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              chunks: {chunkCount}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditableSummaryWidget;
